import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Radar, Link2, Unlink, Settings2 } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { Vehicle } from "../../lib/types";
import { Card } from "../core/Layout";
import { Button } from "../core/Button";
import { Select } from "../core/Field";
import { Pill } from "../core/Badges";

interface Device { id: string; name?: string; serial?: string; plate?: string; linked_vehicle?: { id: string; plate_number: string } | null; suggested_vehicle?: { id: string } | null }
interface Resp { connected: boolean; mock?: boolean; devices: Device[] }

export function VehicleDeviceLink({ v }: { v: Vehicle }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isBO = user?.user_type === "super_admin";
  const canEdit = !isBO && user?.role !== "staff";
  const params = { tenant_id: isBO ? v.tenant_id : undefined };
  const { data, isLoading } = useQuery<Resp>({ queryKey: ["telematics-devices", isBO ? v.tenant_id : ""], queryFn: async () => (await api.get("/telematics/devices", { params })).data, retry: false });
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const current = data?.devices.find((d) => d.id === v.geotab_device_id);
  const free = (data?.devices || []).filter((d) => !d.linked_vehicle);
  const suggested = free.find((d) => d.suggested_vehicle?.id === v.id);
  const apply = async (device_id: string | null) => {
    setBusy(true);
    try {
      await api.post("/telematics/link", { vehicle_id: v.id, device_id });
      toast.success(device_id ? "Geotab device linked" : "Geotab device unlinked");
      ["vehicle", "vehicles", "telematics-devices", "telematics-status", "telematics-vehicle", "telematics-positions"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!data?.connected) {
    return <Card title="Geotab device" icon={Radar} testId="vehicle-device-card"><p className="text-sm text-slate-500" data-testid="vehicle-device-not-connected">{isLoading ? "Loading…" : <>Geotab is not connected{isBO ? " for this client." : <>. <Link to="/app/settings/telematics" className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand)] hover:underline"><Settings2 className="h-3 w-3" />Connect under Settings → Telematics connection</Link></>}</>}</p></Card>;
  }
  return (
    <Card title="Geotab device" icon={Radar} testId="vehicle-device-card" actions={data.mock && <Pill className="bg-sky-50 text-sky-800 ring-sky-200">mock sandbox</Pill>}>
      {v.geotab_device_id ? (
        <div className="flex flex-wrap items-center gap-3" data-testid="vehicle-device-linked">
          <div><p className="font-semibold text-slate-900">{current?.name || v.geotab_device_id}</p><p className="font-mono text-[11px] text-slate-500">{current?.serial || v.geotab_device_id}{current?.plate ? ` · Geotab plate ${current.plate}` : ""}</p></div>
          <Pill className="bg-emerald-50 text-emerald-700 ring-emerald-200">linked</Pill>
          {canEdit && <Button size="sm" variant="ghost" loading={busy} onClick={() => apply(null)} data-testid="vehicle-device-unlink"><Unlink className="h-3.5 w-3.5 text-rose-600" />Unlink</Button>}
        </div>
      ) : canEdit ? (
        <div className="space-y-2" data-testid="vehicle-device-picker">
          <p className="text-xs text-slate-500">{suggested ? <>Device <span className="font-mono font-semibold">{suggested.plate}</span> matches this plate number.</> : "No device with a matching plate number — choose one manually."}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Select data-testid="vehicle-device-select" className="h-9 w-72 text-sm" value={sel || suggested?.id || ""} onChange={(e) => setSel(e.target.value)}>
              <option value="">Choose a Geotab device…</option>
              {free.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}{d.plate ? ` · ${d.plate}` : ""}{d.serial ? ` (${d.serial})` : ""}</option>)}
            </Select>
            <Button size="sm" loading={busy} disabled={!(sel || suggested?.id)} onClick={() => apply(sel || suggested!.id)} data-testid="vehicle-device-link"><Link2 className="h-3.5 w-3.5" />Link device</Button>
          </div>
          {free.length === 0 && <p className="text-xs text-amber-700">All Geotab devices are already linked to other vehicles.</p>}
        </div>
      ) : <p className="text-sm text-slate-400" data-testid="vehicle-device-none">Not linked to a Geotab device.</p>}
    </Card>
  );
}
